import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { b2bGetAccounts, b2bCreateAccount, b2bDeleteAccount } from '../../../lib/adminApi';
import AdminSidebar from '../../../components/AdminSidebar';

const NAVY = '#0a2540'; const ORANGE = '#F37428'; const BLUE = '#0176CE';

function fmtRevenue(n) {
  if (!n) return '—';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(1) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return '$' + (n / 1e3).toFixed(0) + 'K';
  return '$' + n;
}

export default function B2BAccounts() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ company_name: '', website: '', industry: '', erp_system: '', city: '', state: '', country: '', employee_count: '' });
  const [saving, setSaving] = useState(false);
  const [sortCol, setSortCol] = useState('created_on_utc');
  const [sortDir, setSortDir] = useState('desc');

  function load() {
    setLoading(true);
    b2bGetAccounts(search).then(r => { setAccounts(r.accounts || []); setLoading(false); }).catch(() => setLoading(false));
  }

  useEffect(() => { load(); }, [search]);

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  const sorted = [...accounts].sort((a, b) => {
    const av = a[sortCol] ?? ''; const bv = b[sortCol] ?? '';
    return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
  });

  async function handleCreate(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const r = await b2bCreateAccount({ ...form, employee_count: form.employee_count ? parseInt(form.employee_count) : null });
      navigate(`/admin/b2b/accounts/${r.account_id}`);
    } catch (err) { alert(err.message); setSaving(false); }
  }

  function ind(col) { if (sortCol !== col) return ' ↕'; return sortDir === 'asc' ? ' ↑' : ' ↓'; }

  const TH = ({ col, label }) => (
    <th style={s.th} onClick={() => toggleSort(col)} title="Click to sort">
      {label}{ind(col)}
    </th>
  );

  return (
    <div style={s.page}>
      <AdminSidebar active="b2b-accounts" />
      <main style={s.main}>
        <div style={s.topRow}>
          <h1 style={s.title}>Accounts</h1>
          <button style={s.primaryBtn} onClick={() => setShowNew(true)}>+ New Account</button>
        </div>

        <div style={s.searchRow}>
          <input style={s.search} placeholder="Search accounts…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>

        {showNew && (
          <div style={s.modal}>
            <div style={s.modalBox}>
              <h2 style={{ margin: '0 0 16px', color: NAVY, fontSize: 18 }}>New Account</h2>
              <form onSubmit={handleCreate} style={{ display: 'grid', gap: 10 }}>
                <input required style={s.inp} placeholder="Company Name *" value={form.company_name} onChange={e => setForm(f => ({ ...f, company_name: e.target.value }))} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input style={s.inp} placeholder="Website" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} />
                  <input style={s.inp} placeholder="ERP System" value={form.erp_system} onChange={e => setForm(f => ({ ...f, erp_system: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <input style={s.inp} placeholder="Industry" value={form.industry} onChange={e => setForm(f => ({ ...f, industry: e.target.value }))} />
                  <input style={s.inp} placeholder="Employees" type="number" value={form.employee_count} onChange={e => setForm(f => ({ ...f, employee_count: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <input style={s.inp} placeholder="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
                  <input style={s.inp} placeholder="State" value={form.state} onChange={e => setForm(f => ({ ...f, state: e.target.value }))} />
                  <input style={s.inp} placeholder="Country" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} />
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                  <button type="button" style={s.cancelBtn} onClick={() => setShowNew(false)}>Cancel</button>
                  <button type="submit" style={s.primaryBtn} disabled={saving}>{saving ? 'Saving…' : 'Create'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                <TH col="company_name" label="Company" />
                <TH col="standard_industry" label="Industry" />
                <TH col="size_bucket" label="Size" />
                <TH col="annual_revenue" label="Revenue" />
                <TH col="employee_count" label="Employees" />
                <TH col="city" label="Location" />
                <TH col="contact_count" label="Contacts" />
                <TH col="created_on_utc" label="Added" />
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} style={s.empty}>Loading…</td></tr>}
              {!loading && sorted.length === 0 && <tr><td colSpan={7} style={s.empty}>No accounts found</td></tr>}
              {sorted.map(a => (
                <tr key={a.account_id} style={s.tr}>
                  <td style={{ ...s.td, fontWeight: 600, color: NAVY }}>
                    <Link to={`/admin/b2b/accounts/${a.account_id}`} style={{ color: NAVY, textDecoration: 'none' }}>{a.company_name}</Link>
                  </td>
                  <td style={s.td}>{a.standard_industry || a.industry || '—'}</td>
                  <td style={{ ...s.td, color: '#64748b', fontSize: 12 }}>{a.size_bucket || '—'}</td>
                  <td style={{ ...s.td, color: '#059669', fontWeight: 500 }}>{fmtRevenue(a.annual_revenue)}</td>
                  <td style={{ ...s.td, color: '#64748b' }}>{a.employee_count?.toLocaleString() || '—'}</td>
                  <td style={s.td}>{[a.city, a.state].filter(Boolean).join(', ') || '—'}</td>
                  <td style={{ ...s.td, textAlign: 'center' }}>{a.contact_count}</td>
                  <td style={{ ...s.td, color: '#94a3b8', fontSize: 12 }}>{new Date(a.created_on_utc).toLocaleDateString()}</td>
                  <td style={s.td}>
                    <button style={s.deleteBtn} onClick={async () => { if (confirm('Delete account?')) { await b2bDeleteAccount(a.account_id); load(); } }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

const s = {
  page: { display: 'flex', minHeight: '100vh', fontFamily: "'Inter', system-ui, sans-serif" },
  main: { flex: 1, background: '#f0f5fb', padding: '36px 40px', overflowY: 'auto' },
  topRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  title: { color: NAVY, fontSize: 24, fontWeight: 700, margin: 0 },
  searchRow: { marginBottom: 16 },
  search: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: 280 },
  primaryBtn: { background: ORANGE, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, cursor: 'pointer' },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#94a3b8', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 14px', textAlign: 'left', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' },
  tr: { borderBottom: '1px solid #f1f5f9' },
  td: { color: '#374151', fontSize: 13, padding: '10px 14px', verticalAlign: 'middle' },
  empty: { color: '#94a3b8', fontSize: 13, padding: '32px', textAlign: 'center' },
  badge: { background: 'rgba(1,118,206,0.08)', color: BLUE, borderRadius: 4, padding: '2px 8px', fontSize: 11, fontWeight: 600 },
  deleteBtn: { background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: 12 },
  modal: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modalBox: { background: '#fff', borderRadius: 12, padding: '28px 32px', width: 520, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' },
  inp: { padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, width: '100%', boxSizing: 'border-box' },
};
